import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { useToast } from '../../../Hooks/ToasContext'
import { ListHCPArtesColunaUseCase } from '../../../Usecase/HCP/Artes/list-hcp-artes-coluna-usecase'
import { HCPArteKanbanColumnKeyEnum, IHCPArteKanbanItem } from '../../../Services/HCP/creativeflow/creative-flow.interface'
import {
  ITiragemProdutoConfig,
  getConfig,
  saveConfig,
  getProdutoConfig,
} from '../../../Config/hcp-tiragem-config'
import { isAfter, parseISO, startOfDay } from 'date-fns'

export type AbaMontagemType = 'sem_os' | 'com_os'
export type FiltroItensPendentesType = 'todos' | 'completos' | 'com_pendencias'

export interface IMontagemGrupoProduto {
  produto_nome: string
  totalTiragem: number
  minimoTiragem: number
  meiaCartela?: number
  podeFecharCartela: boolean
  meiaCartelaDisponivel: boolean
  qtdMeiaCartelas: number
  itens: IHCPArteKanbanItem[]
}

export interface IMontagemGrupoOS {
  ordem_de_servico: string
  produto_nome: string
  etapa: string
  prazo_producao: string
  data_inicio: string
  totalTiragem: number
  atrasado: boolean
  itens: IHCPArteKanbanItem[]
}

interface IUseMontagemCartelasProps {
  listHCPArtesColunaUseCase: ListHCPArtesColunaUseCase
}

const AUTO_REFRESH_MS = 5 * 60 * 1000
const SEM_OS_PAGE_SIZE = 100   // sem_os: poucos itens, carrega tudo sequencialmente
const COM_OS_PAGE_SIZE = 50    // com_os: muitos itens, carrega lazy

function getPendentesParam(filtro: FiltroItensPendentesType): boolean | undefined {
  if (filtro === 'completos') return false
  if (filtro === 'com_pendencias') return true
  return undefined
}

// Busca todas as páginas SEQUENCIALMENTE — evita disparar centenas de requests paralelos
async function fetchTodasPaginasSeq(
  useCase: ListHCPArtesColunaUseCase,
  temOs: boolean,
  itensPendentes?: boolean
): Promise<{ items: IHCPArteKanbanItem[]; totalCount: number } | null> {
  const allItems: IHCPArteKanbanItem[] = []
  let page = 1
  let hasMore = true
  let totalCount = 0

  while (hasMore) {
    const r = await useCase.handle({
      status: HCPArteKanbanColumnKeyEnum.APROVADO_FINAL,
      tem_os: temOs,
      page,
      page_size: SEM_OS_PAGE_SIZE,
      itens_pendentes: itensPendentes,
    })
    if (r.isFailure) return null
    const d = r.getValue()!
    totalCount = d.count
    allItems.push(...d.results)
    hasMore = d.next !== null
    page++
  }

  return { items: allItems, totalCount }
}

function getTiragem(item: IHCPArteKanbanItem): number {
  return item.tiragem ?? (item.producao?.tiragem ? parseInt(item.producao.tiragem, 10) : 0)
}

export function useMontagemCartelas({ listHCPArtesColunaUseCase }: IUseMontagemCartelasProps) {
  const [aba, setAba] = useState<AbaMontagemType>('sem_os')
  const [loading, setLoading] = useState(false)
  const [loadingMore, setLoadingMore] = useState(false)

  // sem_os: carregamos tudo (poucos itens — tipicamente < 500)
  const [itensSemOS, setItensSemOS] = useState<IHCPArteKanbanItem[]>([])

  // com_os: lazy — página a página
  const [itensComOS, setItensComOS] = useState<IHCPArteKanbanItem[]>([])
  const [comOSTotal, setComOSTotal] = useState(0)
  const [comOSPage, setComOSPage] = useState(1)
  const [comOSHasMore, setComOSHasMore] = useState(false)

  const [lastUpdated, setLastUpdated] = useState<Date | undefined>(undefined)
  const [filtroBusca, setFiltroBusca] = useState('')
  const [filtroItensPendentes, setFiltroItensPendentes] = useState<FiltroItensPendentesType>('todos')
  const initialFetchDoneRef = useRef(false)
  const [tiragemConfig, setTiragemConfig] = useState<Record<string, ITiragemProdutoConfig>>(() => getConfig())
  const [configDialogOpen, setConfigDialogOpen] = useState(false)
  const { showToast } = useToast()

  const fetchDados = async () => {
    setLoading(true)

    // sem_os e com_os página 1 em paralelo, depois sem_os continua sequencialmente se necessário
    const itensPendentesParam = getPendentesParam(filtroItensPendentes)
    const [semOSFirst, comOSResult] = await Promise.all([
      listHCPArtesColunaUseCase.handle({
        status: HCPArteKanbanColumnKeyEnum.APROVADO_FINAL,
        tem_os: false,
        page: 1,
        page_size: SEM_OS_PAGE_SIZE,
        itens_pendentes: itensPendentesParam,
      }),
      listHCPArtesColunaUseCase.handle({
        status: HCPArteKanbanColumnKeyEnum.APROVADO_FINAL,
        tem_os: true,
        page: 1,
        page_size: COM_OS_PAGE_SIZE,
        itens_pendentes: itensPendentesParam,
      }),
    ])

    if (semOSFirst.isFailure) {
      showToast(String(semOSFirst?.error?.error ?? 'Erro ao carregar dados sem OS'), 'error')
      setLoading(false)
      return
    }
    if (comOSResult.isFailure) {
      showToast(String(comOSResult?.error?.error ?? 'Erro ao carregar OS abertas'), 'error')
      setLoading(false)
      return
    }

    const semOSPage1 = semOSFirst.getValue()!
    const comOSPage1 = comOSResult.getValue()!

    // com_os: salva só a primeira página (lazy)
    setItensComOS(comOSPage1.results)
    setComOSTotal(comOSPage1.count)
    setComOSPage(1)
    setComOSHasMore(comOSPage1.next !== null)

    // sem_os: se tiver mais páginas, busca sequencialmente (poucos itens)
    if (!semOSPage1.next) {
      setItensSemOS(semOSPage1.results)
    } else {
      const todosSemOS = await fetchTodasPaginasSeq(listHCPArtesColunaUseCase, false, itensPendentesParam)
      if (!todosSemOS) {
        showToast('Erro ao carregar páginas de sem OS', 'error')
        setLoading(false)
        return
      }
      setItensSemOS(todosSemOS.items)
    }

    setLastUpdated(new Date())
    setLoading(false)
    initialFetchDoneRef.current = true
  }

  const loadMoreComOS = async () => {
    if (loadingMore || !comOSHasMore) return
    setLoadingMore(true)

    const r = await listHCPArtesColunaUseCase.handle({
      status: HCPArteKanbanColumnKeyEnum.APROVADO_FINAL,
      tem_os: true,
      page: comOSPage + 1,
      page_size: COM_OS_PAGE_SIZE,
      itens_pendentes: getPendentesParam(filtroItensPendentes),
    })

    if (!r.isFailure) {
      const d = r.getValue()!
      setItensComOS((prev) => [...prev, ...d.results])
      setComOSPage((p) => p + 1)
      setComOSHasMore(d.next !== null)
    }

    setLoadingMore(false)
  }

  const salvarConfiguracao = useCallback((novaConfig: Record<string, ITiragemProdutoConfig>) => {
    saveConfig(novaConfig)
    setTiragemConfig(novaConfig)
    setConfigDialogOpen(false)
  }, [])

  useEffect(() => {
    const interval = setInterval(fetchDados, AUTO_REFRESH_MS)
    return () => clearInterval(interval)
  }, [])

  // Quando o filtro de pendentes muda, re-busca na API com o novo parâmetro
  useEffect(() => {
    if (!initialFetchDoneRef.current) return
    setItensSemOS([])
    setItensComOS([])
    setComOSTotal(0)
    setComOSPage(1)
    setComOSHasMore(false)
    fetchDados()
  }, [filtroItensPendentes])

  // ── Aba "Aguardando OS" ───────────────────────────────────────────────────
  const grupos = useMemo((): IMontagemGrupoProduto[] => {
    const map = new Map<string, IHCPArteKanbanItem[]>()
    itensSemOS.forEach((item) => {
      if (!map.has(item.produto_nome)) map.set(item.produto_nome, [])
      map.get(item.produto_nome)!.push(item)
    })

    const lista: IMontagemGrupoProduto[] = []
    map.forEach((grupoItens, produto_nome) => {
      const totalTiragem = grupoItens.reduce((acc, item) => acc + getTiragem(item), 0)
      const cfg = getProdutoConfig(produto_nome, tiragemConfig)
      const minimoTiragem = cfg.minimoCartela
      const meiaCartela = cfg.meiaCartela
      const podeFecharCartela = totalTiragem >= minimoTiragem
      const qtdMeiaCartelas = meiaCartela ? Math.floor(totalTiragem / meiaCartela) : 0
      const meiaCartelaDisponivel = !podeFecharCartela && qtdMeiaCartelas >= 1

      lista.push({
        produto_nome, totalTiragem, minimoTiragem, meiaCartela,
        podeFecharCartela, meiaCartelaDisponivel, qtdMeiaCartelas,
        itens: grupoItens,
      })
    })

    lista.sort((a, b) => {
      if (a.podeFecharCartela !== b.podeFecharCartela) return a.podeFecharCartela ? -1 : 1
      if (a.meiaCartelaDisponivel !== b.meiaCartelaDisponivel) return a.meiaCartelaDisponivel ? -1 : 1
      return b.totalTiragem - a.totalTiragem
    })

    return lista
  }, [itensSemOS, tiragemConfig])

  // ── Aba "Em Produção" ─────────────────────────────────────────────────────
  const gruposOS = useMemo((): IMontagemGrupoOS[] => {
    const map = new Map<string, IHCPArteKanbanItem[]>()
    itensComOS.forEach((item) => {
      const key = item.producao?.ordem_de_servico ?? item.ordem_de_servico ?? 'Sem OS'
      if (!map.has(key)) map.set(key, [])
      map.get(key)!.push(item)
    })

    const lista: IMontagemGrupoOS[] = []
    map.forEach((osItens, ordem_de_servico) => {
      const totalTiragem = osItens.reduce((acc, item) => acc + getTiragem(item), 0)
      const prazos = osItens.map((i) => i.producao?.prazo_producao).filter(Boolean) as string[]
      const prazo_producao = prazos.sort().at(-1) ?? ''
      const hoje = startOfDay(new Date())
      const atrasado = prazo_producao !== '' && !isAfter(parseISO(prazo_producao), hoje)
      const data_inicio = osItens.map((i) => i.producao?.data_inicio).filter(Boolean).sort()[0] ?? ''
      const primeiro = osItens.find((i) => i.producao)
      const etapa = primeiro?.producao?.etapa ?? ''
      const produto_nome = primeiro?.produto_nome ?? ''
      lista.push({ ordem_de_servico, produto_nome, etapa, prazo_producao, data_inicio, totalTiragem, atrasado, itens: osItens })
    })

    lista.sort((a, b) => {
      if (a.atrasado !== b.atrasado) return a.atrasado ? -1 : 1
      return a.prazo_producao.localeCompare(b.prazo_producao)
    })
    return lista
  }, [itensComOS])

  // ── Produtos únicos para o dialog ─────────────────────────────────────────
  const produtosDisponiveis = useMemo(() => {
    const set = new Set<string>()
    itensSemOS.forEach((i) => set.add(i.produto_nome))
    Object.keys(tiragemConfig).forEach((k) => set.add(k))
    return Array.from(set).sort()
  }, [itensSemOS, tiragemConfig])

  // ── Filtros ───────────────────────────────────────────────────────────────
  const gruposFiltrados = useMemo(() => {
    const busca = filtroBusca.trim().toLowerCase()
    if (!busca) return grupos
    return grupos.filter((g) => g.produto_nome.toLowerCase().includes(busca))
  }, [grupos, filtroBusca])

  const gruposOSFiltrados = useMemo(() => {
    const busca = filtroBusca.trim().toLowerCase()
    if (!busca) return gruposOS
    return gruposOS.filter(
      (g) => g.ordem_de_servico.toLowerCase().includes(busca) || g.produto_nome.toLowerCase().includes(busca)
    )
  }, [gruposOS, filtroBusca])

  return {
    aba, setAba,
    grupos: gruposFiltrados,
    gruposOS: gruposOSFiltrados,
    totalSemOS: itensSemOS.length,
    totalComOS: comOSTotal,
    comOSCarregados: itensComOS.length,
    comOSHasMore,
    loadingMore,
    loadMoreComOS,
    loading, lastUpdated,
    filtroBusca, setFiltroBusca,
    filtroItensPendentes, setFiltroItensPendentes,
    fetchDados,
    tiragemConfig,
    produtosDisponiveis,
    configDialogOpen,
    setConfigDialogOpen,
    salvarConfiguracao,
  }
}
